/*
	Sometimes we need data that may already exist in the store that we'd prefer to use a selector for,
	rather than dispatch a fetch request.

	e.g. If we have a blade to edit a Version, and the Version data is already in the grid behind it,
	we'd rather just take it from the store, but if we're on the wrong page of Versions, and the item
	isn't in the grid, we have to fetch the Version from the API.

	`useFetchFallback` returns data found either through selection from the store, or that is returned
	via some other means.

	It takes a hash of props to configure its behavior:

		selector - A selector function that returns the desired data if it's in the store, or
			null/undefined otherwise.

		fetcher - A function that returns a promise that resolves to the desired data.  This will
			generally be something that dispatches an API request, but it doesn't need to be.

		shouldFetch - A boolean value that you can pass in as `false` if you want to prevent fetching.

			e.g. If someone reloads the page on the edit Version blade route, you may want to wait for
			the background grid to finish loading before dispatching any fetch requests, because the
			desired data may already be incoming.  In this case, you can set `shouldFetch` equal to the
			`isLoaded` flag value for the grid.

			When the `shouldFetch` value changes, the hook will automatically attempt to get
			the data again.
*/

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

export default function useFetchFallback({
	selector,
	fetcher,
	shouldFetch = true,
}){
	const selected = useSelector(selector);
	const [data, setData] = useState(selected);

	useEffect(() => {
		if((selected !== null) && (selected !== undefined)){
			setData(selected);
		} else {
			if(shouldFetch){
				fetcher().then((fetchedData) => {
					setData(fetchedData);
				});
			}
		}
	}, [selected, shouldFetch]);

	return data;
}
